import { useState } from 'react';
import axios from 'axios';
import { PasswordRules } from '../components/auth/PasswordRules';
import { isStrongPassword, PASSWORD_RULE_MSG } from '../lib/passwordPolicy';
import { useAuth } from '../context/AuthContext';
import { DevicesCard } from '../components/profile/DevicesCard';
import { ProfileEditForm, ProfileEditButton } from '../components/profile/ProfileEditForm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { User, Lock, Eye, EyeOff, Shield, MapPin, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProfilePage = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Yeni sifreler eslesmiyor');
      return;
    }
    
    if (!isStrongPassword(newPassword)) {
      toast.error(PASSWORD_RULE_MSG);
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(API + '/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('Sifreniz basariyla degistirildi');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Sifre degistirme basarisiz';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const userName = user?.name || '';
  const userEmail = user?.email || '';
  const userRole = user?.role || 'user';
  const isSuperAdmin = user?.is_super_admin || false;
  const allowedLocs = user?.allowed_locations || [];

  const getRoleLabel = (role) => {
    const labels = {
      super_admin: 'Super Admin',
      location_manager: 'Lokasyon Yoneticisi',
      isg_expert: 'ISG Uzmani',
      viewer: 'Goruntuleyici',
      firma_yetkilisi: 'Firma Yetkilisi',
      '2_onayci': '2. Onaycı',
      user: 'Kullanici'
    };
    return labels[role] || role;
  };

  return (
    <div data-testid="profile-page" className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Profil</h1>
        <p className="text-slate-500 mt-1">Hesap bilgilerinizi goruntuleyebilir ve sifrenizi degistirebilirsiniz</p>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Kullanici Bilgileri
            {!editing && <ProfileEditButton onClick={() => setEditing(true)} />}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {editing ? <ProfileEditForm onClose={() => setEditing(false)} /> : (
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center">
              <span className="text-2xl font-semibold text-slate-600">
                {userName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{userName}</h3>
              <p className="text-slate-500" data-testid="profile-email">{userEmail}</p>
              {user?.phone && <p className="text-sm text-slate-500" data-testid="profile-phone">{user.phone}</p>}
            </div>
          </div>
          )}
          
          <div className="pt-4 border-t border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Rol</Label>
                <div className="mt-1">
                  <Badge className={isSuperAdmin ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}>
                    {isSuperAdmin && <Shield className="h-3 w-3 mr-1" />}
                    {getRoleLabel(userRole)}
                  </Badge>
                </div>
              </div>
              
              {allowedLocs.length > 0 && !isSuperAdmin && (
                <div>
                  <Label className="text-slate-500">Yetkili Lokasyonlar</Label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {allowedLocs.map((locId, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        <MapPin className="h-3 w-3 mr-1" />
                        {locId}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {isSuperAdmin && (
                <div>
                  <Label className="text-slate-500">Yetkili Lokasyonlar</Label>
                  <p className="text-sm text-emerald-600 mt-1">Tum lokasyonlar</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Sifre Degistir
          </CardTitle>
          <CardDescription>
            Hesabinizin guvenligi icin guclu bir sifre kullanin
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Mevcut Sifre</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Mevcut sifrenizi girin"
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="newPassword">Yeni Sifre</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Yeni sifrenizi girin"
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordRules password={newPassword} testId="profile-password-rules" />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Yeni Sifre (Tekrar)</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Yeni sifrenizi tekrar girin"
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="bg-slate-900 hover:bg-slate-800"
              disabled={loading}
              data-testid="change-password-btn"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {loading ? 'Degistiriliyor...' : 'Sifreyi Degistir'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <DevicesCard />
    </div>
  );
};

export default ProfilePage;
